import React from 'react';
import { Calendar, Check, Clock, AlertTriangle } from 'lucide-react';

const PaymentScheduleCard = ({ echeances }) => {
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Payé':
        return <Check className="h-5 w-5 text-green-500" />;
      case 'En retard':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      default:
        return <Clock className="h-5 w-5 text-blue-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Payé':
        return 'bg-green-50 border-green-200';
      case 'En retard':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Trier les échéances: d'abord les payées, puis les à venir, puis les en retard
  const sortedEcheances = [...(echeances || [])].sort((a, b) => {
    // Ordre de priorité: 1. Payé, 2. À venir, 3. En retard
    const getStatusPriority = (status) => {
      switch (status) {
        case 'Payé': return 1;
        case 'À venir': return 2;
        case 'En retard': return 3;
        default: return 4;
      }
    };

    const priorityA = getStatusPriority(a.status);
    const priorityB = getStatusPriority(b.status);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    // Si même priorité, trier par date d'échéance (plus récente d'abord)
    return new Date(a.date_echeance) - new Date(b.date_echeance);
  });

  // Diviser les échéances par statut pour l'affichage
  const paidEcheances = sortedEcheances.filter(e => e.status === 'Payé');
  const upcomingEcheances = sortedEcheances.filter(e => e.status === 'À venir');
  const overdueEcheances = sortedEcheances.filter(e => e.status === 'En retard');

  // Fonction pour afficher une échéance
  const renderEcheance = (echeance) => (
    <div
      key={echeance.id}
      className={`flex items-center justify-between p-4 rounded-lg border ${getStatusColor(echeance.status)} transition-all hover:shadow-md`}
    >
      <div className="flex items-start space-x-4">
        {getStatusIcon(echeance.status)}
        <div>
          <h3 className="font-medium text-gray-900">
            {echeance.type_echeance_display}
          </h3>
          <p className="text-sm text-gray-500">
            Échéance: {formatDate(echeance.date_echeance)}
          </p>
          {echeance.option_details && (
            <p className="text-sm text-gray-500">
              Option: {echeance.option_details.libelle}
            </p>
          )}
        </div>
      </div>
      <div className="text-right">
        <p className="font-semibold text-gray-900">
          {echeance.montant.toLocaleString()} FCFA
        </p>
        <span className={`text-sm px-2 py-1 rounded-full inline-block ${
          echeance.status === 'Payé' ? 'bg-green-100 text-green-800' : 
          echeance.status === 'En retard' ? 'bg-red-100 text-red-800' : 
          'bg-blue-100 text-blue-800'
        }`}>
          {echeance.status}
        </span>
      </div>
    </div>
  );

  return (
    <div className="bg-white h-full">
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center space-x-2">
          <Calendar className="h-5 w-5 text-[#202654]" />
          <h2 className="text-lg font-semibold text-gray-900">Échéancier de paiement</h2>
        </div>
      </div>
      
      <div className="p-6 overflow-y-auto max-h-[calc(100vh-220px)]">
        {sortedEcheances.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            Aucune échéance trouvée
          </div>
        ) : (
          <div className="space-y-6">
            {paidEcheances.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-gray-500 uppercase tracking-wider">Paiements effectués</h3>
                <div className="space-y-3">
                  {paidEcheances.map(renderEcheance)}
                </div>
              </div>
            )}

            {upcomingEcheances.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-gray-500 uppercase tracking-wider">Paiements à venir</h3>
                <div className="space-y-3">
                  {upcomingEcheances.map(renderEcheance)}
                </div>
              </div>
            )}

            {overdueEcheances.length > 0 && (
              <div className="space-y-3">
                <h3 className="font-medium text-sm text-gray-500 uppercase tracking-wider">Paiements en retard</h3>
                <div className="space-y-3">
                  {overdueEcheances.map(renderEcheance)}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PaymentScheduleCard;
